import { merge, cloneDeep } from 'lodash';
import { CreativeUnitPackageDto } from '../models/creative-unit-package.dto';
import { CreativeUnitDto, CreativeUnitWithCustomizationsDto } from '../models/creative-unit/creative-unit.dto';
import { LayersUtils } from './layers.utils';
import { LayerDto, LayerType } from '../models/layer/layer.dto';
import { CustomizationUtils } from './customization.utils';
import { PublicBusiness } from '../../business/business.entity';
import { MergePropertiesUtils } from './merge-properties.utils';
import { ObjectUtils } from '../../_core/utils/utils.object';
import { CreativeUnitUtils } from './creative-unit.utils';
import { CreativeUnitListingDto } from '../models/creative-unit/creative-unit-listing.dto';
import { CustomizationGroupDto, CustomizationImagePickerDto, CustomizationItemDto, CustomizationTextDto, CustomizationType, CustomizationVideoPickerDto, CustomizationAudioPickerDto } from '../models/customization/customization.dto';
import { ConditionRelation } from '../models/visibility-condition.dto';
import { CreativeUnitPackage, Status as CreativeUnitStatus } from "../creative-unit-package.entity";
import { createLayoutGroupDefaults, mergeLayoutGroup, normalizeLayoutGroup } from '../models/layer/layout-group.dto';

export interface CreativePackageMigrationConfig {
	/**
	 * Should we add in the interactivity settings?
	 */
	interactivity?: boolean;

	/**
	 * Should we add in the customizations?
	 */
	placeholderImages?: boolean;
}

export class CreativePackageUtils {

	public static defaultStyles = `
	/* Global Style settings */
	body,
	.render-item,
	.ad-container {
		/* Set up for using container queries */
 		container-type: size;
    	container-name: main;

		/* Variables */
		--primary-color: #000;
	}

	.ad-container {
		/* Global Defaults */
		color: white;
		font-size: 16px; /* Default font size for 1em */
		line-height: 1;

		div {
			font-family: Arial, sans-serif;
		}

		p {
			display: block;
		}

		div, p, span, h1, h2, h3, h4, h5, h6 {
			line-height: 1;
		}

		/* For common block elements inside divs, inherit the line-height from the parent */
		div > :is(p, span, h1, h2, h3, h4, h5, h6) {
			line-height: inherit;
		}

		/* Utility Classes */
		.hidden {
			display: none;
		}
	}
`;

	public static generateCustomizationsFromLayers(layers: LayerDto[] | undefined): CustomizationItemDto[] {
		if (!layers || layers.length === 0) {
			return [];
		}

		return layers
			.map(layer => {
				let customization: CustomizationItemDto | undefined = {
					id: new Date().getTime().toString() + Math.random().toString(),
					label: layer.label || layer.id,
					type: undefined
				};

				switch (layer.type) {
					case 'group':
						customization = {
							...customization,
							type: CustomizationType.GROUP,
							children: this.generateCustomizationsFromLayers(layer.layers || [])
						} as CustomizationGroupDto;
						break;

					case 'text':
					case 'button':
					case 'html':
						customization = {
							...customization,
							type: CustomizationType.TEXT,
							layerIds: [layer.id],
							template: 'text',
							loadDefaultText: false,
							enableWritingTools: true
						} as CustomizationTextDto;

						if (layer.text?.content?.length > 30) {
							customization.template = 'textarea';
						}

						if (layer.type === 'html' || layer.text?.content?.match(/<[^>]+>/)) {
							customization.template = 'wysiwyg';
						}
						break;

					case 'image':
						customization = {
							...customization,
							type: CustomizationType.IMAGE_PICKER,
							layerIds: [layer.id],
							dataSources: ['uploads'],
							showSelectButton: true,
							selectButtonLabel: 'Select Image'
						} as CustomizationImagePickerDto;
						break;

					case 'video':
						customization = {
							...customization,
							type: CustomizationType.VIDEO_PICKER,
							layerIds: [layer.id],
							dataSources: ['uploads']
						} as CustomizationVideoPickerDto;
						break;

					case 'audio':
						customization = {
							...customization,
							type: CustomizationType.AUDIO_PICKER,
							layerIds: [layer.id],
							dataSources: ['uploads']
						} as CustomizationAudioPickerDto;
						break;

					default:
						customization = undefined;
						break;
				}

				return customization;
			})
			.filter((c): c is CustomizationItemDto => !!c);
	}

	private static ensureGroupStructure(groups: CustomizationItemDto[]): CustomizationItemDto[] {
		return groups
			.filter(group => group.type !== CustomizationType.GROUP || (group.children && group.children.length > 0))
			.map(group => {
				if (group.type !== CustomizationType.GROUP || !group.children) {
					return group;
				}

				const normalizedChildren = this.ensureGroupStructure(group.children);
				return {
					...group,
					children: normalizedChildren
				};
			})
			.filter(group => group.type !== CustomizationType.GROUP || (group.children && group.children.length > 0));
	}

	/**
	 * Creates a Creative Unit with Customizations using package details to fill in the gaps of a Creative Unit.
	 * The function starts with all of the package details, and then applies overrides from the creative unit on top.
	 * It also looks for any places where merge tags exists and processes those.
	 */
	public static createCreativeUnitWithCustomizations(
		pkg: CreativeUnitPackageDto,
		creativeUnit: CreativeUnitWithCustomizationsDto,
		business?: PublicBusiness
	): CreativeUnitWithCustomizationsDto {
		// Create overrides for each layer.  Include nested layers.
		// console.log('Starting Layer Overrides', creativeUnit.layers, pkg.layers);
		// let packageLayers = LayersUtils.mapAllLayers(pkg.layers, layer => {
		// 	let layerOverrides = LayersUtils.findAllLayers(creativeUnit?.layers, creativeUnitLayer => creativeUnitLayer.id === layer.id);
		// 	// console.log('Layer Overrides', layer.label, layer, layerOverrides);
		// 	if (layerOverrides?.length) {
		// 		let layerWithOverrides = merge(cloneDeep(layer), cloneDeep(layerOverrides[0])) as LayerDto;

		// 		// console.log('Layer With Overrides', layerWithOverrides);
		// 		return layerWithOverrides;
		// 	} else {
		// 		return layer;
		// 	}
		// });
		// console.log('createCreativeUnitWithCustomizations before', pkg.layers, creativeUnit?.layers);
		// console.log('[createCreativeUnitWithCustomizations] === BEFORE MERGE ===');
		// console.log('[createCreativeUnitWithCustomizations] Linked Layers:',
		// 	JSON.stringify(pkg?.layers?.map(l => ({ id: l.id, label: l.label, style: l.style })), null, 2)
		// );
		// console.log('[createCreativeUnitWithCustomizations] Creative unit layers:',
		// 	JSON.stringify(creativeUnit?.layers?.map(l => ({ id: l.id, label: l.label, style: l.style })), null, 2)
		// );

		let layers = CreativeUnitUtils.mergePackageLayersWithUnitLayers(pkg?.layers || [], creativeUnit?.layers || []);

		// console.log('[createCreativeUnitWithCustomizations] === AFTER MERGE ===');
		// console.log('[createCreativeUnitWithCustomizations] Merged layers:',
		// 	JSON.stringify(layers?.map(l => ({ id: l.id, label: l.label, style: l.style })), null, 2)
		// );
		// console.log('createCreativeUnitWithCustomizations after', layers);

		// Add any customizations from the package that do not exist on the creative unit.
		// console.log('Starting Customizations', creativeUnit.customizations);
		let customizations = CustomizationUtils.mapAllCustomizations(pkg.customizations, customization => {
			let creativeUnitCustomization = creativeUnit?.customizations?.find(c => c.id === customization.id);
			if (creativeUnitCustomization) {
				return creativeUnitCustomization;
			} else {
				return customization;
			}
		});

		// if (creativeUnit.customizations) {
		// 	customizations = creativeUnit.customizations;
		// }

		// Run styles through merge tags function. Only if we find a {{ ... }} in the style string.
		let styles = ''; // (pkg.styles || '') + (creativeUnit.styles || '');
		if (pkg.styles) {
			if (Array.isArray(pkg.styles)) {
				for (let i = 0; i < pkg.styles.length; i++) {
					styles += pkg.styles[i].styles;
				}
			} else {
				styles += pkg.styles;
			}
		}

		if (creativeUnit?.styles) {
			if (Array.isArray(creativeUnit.styles)) {
				for (let i = 0; i < creativeUnit.styles.length; i++) {
					styles += creativeUnit.styles[i].styles;
				}
			} else {
				styles += creativeUnit.styles;
			}
		}

		if (styles?.length > 0) {
			try {
				let state = MergePropertiesUtils.getMergeableProperties(
					{
						...pkg,
						layers,
						customizations
					},
					business,
					creativeUnit
				);
				// console.log('state', state);
				// if (styles.includes('{{')) {
				// 	styles = MergeTags.applyMergeTagsToString(styles, state);
				// }
			} catch (e) {}
		}

		let variables = { ...(pkg.variables || {}) };
		if (creativeUnit?.variableOverrides) {
			variables = ObjectUtils.unfreeze(variables);
			variables = merge(variables, creativeUnit.variableOverrides);
		}

		let interactivity = { ...pkg.interactivity };
		if (creativeUnit?.interactivity !== null && creativeUnit?.interactivity !== undefined) {
			interactivity = merge(interactivity, creativeUnit.interactivity);
		}

		let background = { ...pkg.background };
		if (creativeUnit?.background) {
			background = merge(background, creativeUnit.background);
    }

		// Handle animationConfig - merge package and unit configs intelligently
		let animationConfig: any = undefined;
		if (pkg?.animationConfig || creativeUnit?.animationConfig) {
			const packageConfig: any = cloneDeep(pkg?.animationConfig || {});
			const unitConfig: any = cloneDeep(creativeUnit?.animationConfig || {});

			// Merge the configs but handle gsapPlugins array specially
			animationConfig = merge(packageConfig, unitConfig);

			// For gsapPlugins, we want to merge the arrays intelligently
			if (packageConfig.gsapPlugins || unitConfig.gsapPlugins) {
				const pluginMap = new Map();

				// Add package plugins first
				if (packageConfig.gsapPlugins) {
					packageConfig.gsapPlugins.forEach((plugin: any) => {
						pluginMap.set(plugin.name, plugin);
					});
				}

				// Override with unit plugins (if they exist)
				if (unitConfig.gsapPlugins) {
					unitConfig.gsapPlugins.forEach((plugin: any) => {
						pluginMap.set(plugin.name, plugin);
					});
				}

				// Convert back to array
				animationConfig.gsapPlugins = Array.from(pluginMap.values());

			}
		}

		return {
			...creativeUnit,
			layers: layers,
			styles: styles,
			containerClasses: CreativeUnitUtils.getContainerClasses(creativeUnit?.containerClasses, pkg.containerClasses, creativeUnit?.appendContainerClasses),
			variables: {
				...pkg.variables,
				...creativeUnit?.variables
			},
			interactivity,
			background,
			theme: pkg.theme,
			customizations: customizations,
			scripts: pkg?.scripts || [],
			exportCropBox: creativeUnit?.exportCropBox,
			htmlConfig: (creativeUnit?.htmlConfig || pkg?.htmlConfig) ? {
				...pkg?.htmlConfig,
				...creativeUnit?.htmlConfig
			} : undefined,
			// Include animationConfig with gsapPlugins
			...(animationConfig && { animationConfig })
		} as CreativeUnitWithCustomizationsDto;
	}

	public static getListingsFromPackage(
		pkg: CreativeUnitPackageDto,
		showHidden = false
	): CreativeUnitListingDto[] {
		if (!pkg) {
			console.error('No Creative Unit Package or Listings provided');
			return;
		}

		// Combine creative units with listings.
		let listingsFromCreativeUnits = (pkg?.creativeUnits || []).map(unit => CreativeUnitUtils.convertUnitToListing(unit));
		let listingsFromPackage = (pkg?.creativeUnitListingSettings?.listings || []);

		return [
			...listingsFromCreativeUnits,
			...listingsFromPackage
		].map(listing => ({
			...listing,
			creativeUnitNames: pkg?.creativeUnits
				?.filter(unit => listing.creativeUnitIds?.includes(unit.id))
				.map(unit => ObjectUtils.getLocale(unit.name, unit.locale?.name))
		}))
		.filter(unit => {
			if (showHidden) {
				return true;
			}

			// Hide hidden units.
			return unit.hidden !== true
		});

	}

	public static sortListings(listings: CreativeUnitListingDto[], pkg: CreativeUnitPackageDto, sortMethod?: string): CreativeUnitListingDto[] {
		sortMethod = sortMethod || pkg?.creativeUnitListingSettings?.sortBy?.value || 'name';

		return listings.sort((a, b) => {
			let aVal = a[sortMethod] || a.metadata?.[sortMethod] || '';
			let bVal = b[sortMethod] || b.metadata?.[sortMethod] || '';

			switch (sortMethod) {
				// Sort by the index of the creative unit in the package.
				case 'created':
					aVal = pkg.creativeUnits.findIndex(unit => unit.id === a.id);
					bVal = pkg.creativeUnits.findIndex(unit => unit.id === b.id);
					break;
			}

			// console.log(sortMethod, aVal, bVal);

			// Handle empty strings explicitly
			if (aVal === '' && bVal !== '') return 1;
			if (aVal !== '' && bVal === '') return -1;
			if (aVal === '' && bVal === '') return 0;

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return aVal.localeCompare(bVal);
			} else {
				return aVal > bVal ? 1 : -1;
			}
		})
		// Sort all the hidden units to the bottom.
		.sort((a, b) => {
			if (a.hidden && !b.hidden) {
				return 1;
			}

			if (!a.hidden && b.hidden) {
				return -1;
			}

			return 0;
		});
	}

	/**
	 * Helper method to sanitize a creative package before saving.
	 * Execute creative unit sanitization as well as validate customization references.
	 * @param pkg
	 */
	public static sanitizeCreativePackage(pkg: Partial<CreativeUnitPackageDto>): Partial<CreativeUnitPackageDto> {
		const creativeUnits = CreativePackageUtils.sanitizeCreativeUnits(pkg.creativeUnits ?? [], pkg);

		return this.validateCustomizationReferences({...pkg, creativeUnits} as CreativeUnitPackageDto);
	}

	/**
	 * Helper method to sanitize the creative units before saving / duplicating.
	 * Make sure all the creative units have at minimum an empty object of all the Linked Layers.
	 */
	public static sanitizeCreativeUnits(creativeUnits: CreativeUnitDto[], pkg: Partial<CreativeUnitPackage>): CreativeUnitDto[] {
		return creativeUnits.map(unit => {
			unit = ObjectUtils.unfreeze(unit);

			if (!unit.layers) {
				unit.layers = [];
			}

			// Make sure units have the proper packageId.
			unit.packageId = pkg.id;

			// Add the theme to the unit.
			unit.theme = pkg.theme;

			// console.log('Sanitize Creative Units Before', unit.layers);

			let updatedLayers = [...unit.layers];
			LayersUtils.mapAllLayers(pkg.layers, (layer, parentId) => {
				let foundMatch = false;

				updatedLayers = LayersUtils.mapAllLayers(updatedLayers, (unitLayer, unitParentId) => {
					if (unitLayer.id === layer.id) {
						foundMatch = true;
						// console.log('Updating Layer', unitLayer.label, unitLayer);
						const nextLayer = {
							...(unitLayer || {}),
							id: layer.id,
							label: layer.label,
							type: layer.type,
							style: unitLayer?.style,
							animation: unitLayer?.animation,
							layers: unitLayer.layers
						} as LayerDto;

						const mergedGroup = this.mergeLayerGroup(layer.group, unitLayer?.group);
						if (mergedGroup) {
							nextLayer.group = mergedGroup;
						}

						return nextLayer;
					} else {
						return unitLayer;
					}
				});

				// If we didn't find a match, add the layer to the unit.
				if (!foundMatch) {
					updatedLayers = this.addLayerToUnitLayers(updatedLayers, {
						id: layer.id,
						label: layer.label,
						type: layer.type,
						group: this.mergeLayerGroup(layer.group, undefined)
					}, parentId);
				}

				return layer;
			});

			// Now loop through the updated Layers and remove any layers that don't exist in the package.
			updatedLayers = LayersUtils.filterAllLayers(updatedLayers, (unitLayer, unitParentId) => {
				if (!unitLayer.creativeUnitId && LayersUtils.findAllLayers(pkg.layers, layer => layer.id === unitLayer.id).length === 0) {
					// console.log('Removing Layer', unitLayer.label, unitLayer);
					return false;
				} else {
					return true;
				}
			})

			// console.log('Sanitize Creative Units After', updatedLayers);

			// Merge the layers.
			unit.layers = updatedLayers;


			return unit;
		});
	}

	private static mergeLayerGroup(baseGroup?: LayerDto['group'], unitGroup?: LayerDto['group']) {
		if (!baseGroup && !unitGroup) {
			return undefined;
		}

		const mergedGroup = {
			...(baseGroup ? cloneDeep(baseGroup) : {}),
			...(unitGroup ? cloneDeep(unitGroup) : {})
		};

		const baseLayout = baseGroup?.layout ? normalizeLayoutGroup(baseGroup.layout) : undefined;
		const unitLayout = unitGroup?.layout ? normalizeLayoutGroup(unitGroup.layout) : undefined;

		if (baseLayout || unitLayout) {
			const layoutBase = baseLayout ?? createLayoutGroupDefaults();
			mergedGroup.layout = unitLayout ? mergeLayoutGroup(layoutBase, unitLayout) : layoutBase;
		}

		return mergedGroup;
	}

        public static addLayerToUnitLayers(unitLayers: LayerDto[], layer: LayerDto, parentId?: string): LayerDto[] {
                if (!parentId) {
                        return [
                                ...(unitLayers || []),
                                layer
                        ];
                }

                // If adding to a parent with timing, inherit its delay and duration
                const parentLayer = LayersUtils.findAllLayers(unitLayers, l => l.id === parentId)?.[0];
                const parentTiming = parentLayer?.timing;
                if (parentTiming && (parentTiming.startOffset !== undefined || parentTiming.duration !== undefined)) {
                        layer = {
                                ...layer,
                                timing: {
                                        startOffset: parentTiming.startOffset,
                                        duration: parentTiming.duration
                                } as any
                        };
                }

                let updatedLayers = LayersUtils.mapAllLayers(unitLayers, (unitLayer, unitParentId) => {
                        if (unitLayer.id === parentId) {
                                return {
                                        ...(unitLayer || {}),
					layers: [
						...(unitLayer?.layers || []),
						layer
					]
				};
			} else {
				return unitLayer;
			}
		});

		return updatedLayers;
	}

	/**
	 * This is a recursive function that will loop through all of the Linked Layers at a certain level and apply the changes to the associated creative unit layers.
	 *
	 * @param packageLayers
	 * @param creativeUnitLayers
	 */
	static upsertPackageLayersIntoCreativeUnit(packageLayers: LayerDto[], creativeUnitLayers: LayerDto[]) {

	}

	/**
	 * Helper method to make sure that templates are up to date with the latest package structure.
	 * This will currently just make sure certain properties exist and catch misspellings from AI,
	 * but in the future could be used to migrate templates to new structures based on the modified date.
	 */
	public static migrateCreativeUnitPackage(pkg: Partial<CreativeUnitPackageDto>, config?: CreativePackageMigrationConfig): CreativeUnitPackageDto {
		if (!pkg) {
			return pkg as CreativeUnitPackageDto;
		}

		// Make sure the package has some level of styles.
		if (!pkg.styles || pkg.styles === '') {
			pkg.styles = this.defaultStyles;
		}

		/**
		 * AI Problems Area
		 */


		if (pkg.layers) {
			pkg.layers = LayersUtils.mapAllLayers(pkg.layers, layer => {
				// Make sure that layers don't has a 'styles' property when it should be 'style'.
				if (layer.styles) {
					layer.style = layer.styles;
					delete layer.styles;
				}

				// Look for shape layers with a backgroundColor or background-color property and make sure they have a color property.
				// This is to make sure the svg fill="currentColor" works properly.
				if (layer.type === 'shape' && (layer.style?.backgroundColor || layer.style?.['background-color'])) {
					layer.style = {
						...layer.style,
						color: layer.style?.color || layer.style?.backgroundColor || layer.style?.['background-color']
					};
				}

				if (layer.group?.layout) {
					layer.group = {
						...layer.group,
						layout: normalizeLayoutGroup(layer.group.layout)
					};
				}

				// Make sure the layer has a certain style properties.
				if (layer.type !== LayerType.GROUP) {
					layer.style = {
						...(layer.style || {}),
						position: layer.style?.position || 'absolute'
					}
				}


				// If this is a smart text layer and we have a font-size style property
				// We need to convert that into the maxFontSize property.
				// TODO: We need to be able to do autosize with container units before we can
				// implement this.
				// if (layer.type === 'text' && layer.text?.smart && (layer.style?.['font-size'] || layer.style?.fontSize)) {
				// 	layer.text.autoSize = {
				// 		...(layer.text.autoSize || {}),
				// 		maxFontSize: layer.style['font-size'] || layer.style.fontSize
				// 	}
				// }

				return layer;
			});

			pkg.creativeUnits = pkg.creativeUnits?.map(unit => {
				if (unit.layers) {
					unit.layers = LayersUtils.mapAllLayers(unit.layers, layer => {
						// Make sure that layers don't has a 'styles' property when it should be 'style'.
						if (layer.styles) {
							layer.style = layer.styles;
							delete layer.styles;
						}

						// Look for shape layers with a backgroundColor or background-color property and make sure they have a color property.
						// This is to make sure the svg fill="currentColor" works properly.
						if (layer.type === 'shape' && (layer.style?.backgroundColor || layer.style?.['background-color'])) {
							layer.style = {
								...layer.style,
								color: layer.style?.color || layer.style?.backgroundColor || layer.style?.['background-color']
							}
						}

						if (layer.group?.layout) {
							layer.group = {
								...layer.group,
								layout: normalizeLayoutGroup(layer.group.layout)
							};
						}

						return layer;
					});
				}
				return unit;
			});
		}



		// Make sure the package has customizations as an array.
		if (!pkg.customizations || !Array.isArray(pkg.customizations)) {
			pkg.customizations = [];
		}

		if (config?.interactivity) {
			// Make sure the package has interactivity settings.
			if (!pkg.interactivity) {
				pkg.interactivity = {
					enabled: true,
					dragInteractivity: true,
					resizeInteractivity: true,
					styleInteractivity: true
				};
			}
		}

		if (config?.placeholderImages) {
			pkg.layers = LayersUtils.mapAllLayers(pkg.layers, layer => {
				if (layer.image?.assetPath) {
					return {
						...layer,
						image: {
							...layer.image,
							assetPath: `https://picsum.photos/seed/${layer.id}/500/500`
						}
					}
				}

				return layer;
			});

			// Iterate and do this for all creative units.
			pkg.creativeUnits = pkg.creativeUnits?.map(unit => {
				if (unit.layers) {
					unit.layers = LayersUtils.mapAllLayers(unit.layers, layer => {
						if (layer.image?.assetPath) {
							return {
								...layer,
								image: {
									...layer.image,
									assetPath: `https://picsum.photos/seed/${layer.id}/500/500`
								}
							}
						} else {
							return layer;
						}
					});
				}
				return unit;
			});
		}

		return pkg as CreativeUnitPackageDto;
	}

	public static prepareCreativeForComparison(obj: CreativeUnitPackageDto): CreativeUnitPackageDto {
		let creativeUnits = CreativePackageUtils.sanitizeCreativeUnits(obj?.creativeUnits ?? [], obj);

		return {
			...obj,
			customizations: CustomizationUtils.mapAllCustomizations(obj?.customizations ?? [], customization => {
				return {
					...customization,
					value: undefined
				};
			}),
			creativeUnits: CreativePackageUtils.remapCreativeUnitsForSave(creativeUnits),
			modified: null
		};
	}

	public static generateCustomizationsFromPackage(pkg: CreativeUnitPackageDto): CustomizationItemDto[] {
		if (!pkg) {
			return [];
		}

		const groups: CustomizationItemDto[] = [];
		const createGroupId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

		const packageCustomizations = this.generateCustomizationsFromLayers(pkg.layers || []);
		if (packageCustomizations.length) {
			groups.push({
				id: createGroupId('group-all-units'),
				label: 'All Units',
				type: CustomizationType.GROUP,
				children: packageCustomizations
			} as CustomizationGroupDto);
		}

		const packageLayerIds = new Set(
			LayersUtils.flattenLayers(pkg.layers || []).map(layer => layer.id)
		);

		pkg.creativeUnits?.forEach(unit => {
			const uniqueLayers = this.extractUniqueUnitLayers(unit.layers || [], packageLayerIds);
			const unitCustomizations = this.generateCustomizationsFromLayers(uniqueLayers);

			if (unitCustomizations.length) {
				groups.push({
					id: createGroupId(`group-${unit.id || 'unit'}`),
					label: unit.name || unit.id || 'Creative Unit',
					type: CustomizationType.GROUP,
					children: unitCustomizations,
					// Show this group only when the active creative unit matches.
					visibilityConditions: [
						{
							path: 'activeCreativeUnit.creativeUnitId',
							relation: ConditionRelation.EQ,
							value: unit.id
						}
					]
				} as CustomizationGroupDto);
			}
		});

		return this.ensureGroupStructure(groups);
	}

	private static extractUniqueUnitLayers(layers: LayerDto[] | undefined, packageLayerIds: Set<string>): LayerDto[] {
		const results: LayerDto[] = [];

		if (!layers || layers.length === 0) {
			return results;
		}

		layers.forEach(layer => {
			const childUnique = this.extractUniqueUnitLayers(layer.layers, packageLayerIds);
			const isUnique = !packageLayerIds.has(layer.id);

			if (isUnique) {
				if (layer.type === LayerType.GROUP && childUnique.length) {
					results.push(...childUnique);
				} else {
					results.push({
						...layer,
						layers: childUnique
					});
				}
			} else {
				results.push(...childUnique);
			}
		});

		return results;
	}

	/**
	 * Helper method to remap the creative units before saving / duplicating.
	 * Make sure all the creative units have the proper dimensions and other properties for saving / duplicating.
	 * */
	public static remapCreativeUnitsForSave(units: CreativeUnitDto[]): CreativeUnitDto[] {
		return units.map(unit => {
			return {
				...unit,

				// Nullify stuff that should come from the package.
				customizations: undefined,
				variables: undefined,
				theme: undefined,

				// Restore the original dimensions if they exist.
				dimensions: {
					...unit.dimensions,
					width: unit.dimensions.originalDimensions?.width || unit.dimensions.width,
					height: unit.dimensions.originalDimensions?.height || unit.dimensions.height,
					ppi: unit.dimensions.originalDimensions?.ppi || unit.dimensions.ppi,
					originalDimensions: undefined
				}
			};
		})
	}

	/**
	 * This method will return a list of unique layers from the package itself and any unique creative unit layers.
	 * The creative unit layers will be appended with their unit name.
	 */
	public static getUniquePkgAndUnitLayers(flatPkgLayers: LayerDto[], creativeUnits: CreativeUnitDto[]): LayerDto[] {
		const layerList = [...flatPkgLayers];

		// Create a set to speed up duplicate lookup.
		const pkgLayerIds = new Set(layerList.map(l => l.id));

		creativeUnits?.forEach((unit) => {
			const creativeUnitLayers = LayersUtils.flattenLayersFromCreativeUnit(unit);

			// Check if the creative unit layers already exist in the merged layers.
			const uniqueUnitLayers = creativeUnitLayers
				.filter(l => !pkgLayerIds.has(l.id))
				.map(l => {
					return {
						...l,
						label: l.label + ` (${unit.name})`
					};
				});

			layerList.push(...uniqueUnitLayers);
		});

		return layerList;
	}

	/**
	 * Helper method to validate customization references in the package.
	 * Remove any invalid layer references and reset customization values if needed.
	 * @param pkg Creative Unit Package
	 * @param layers Optional layers to use instead of package layers.
	 * @return Validated Creative Unit Package
	 */

	public static validateCustomizationReferences(pkg: CreativeUnitPackageDto, layers?: LayerDto[]): CreativeUnitPackageDto {
		if (!pkg?.customizations || pkg.customizations.length === 0) {
			// nothing to validate
			return pkg;
		}

		const flattenedLayers = LayersUtils.flattenLayers(layers || pkg.layers || []);
		const uniqueLayers = CreativePackageUtils.getUniquePkgAndUnitLayers(flattenedLayers, pkg.creativeUnits);

		// Create a lookup map for faster layer access
		const layerMap = new Map(uniqueLayers.map(layer => [layer.id, layer]));

		// Remove invalid layer references
		const validatedCustomizations = CustomizationUtils.removeInvalidLayerReferences(pkg.customizations, uniqueLayers);

		// Reset customization value if it's associated with video layers and used for default value
		const finalCustomizations = CustomizationUtils.mapAllCustomizations(validatedCustomizations, (customization: CustomizationItemDto) => {
			const layerId = customization.layerIds?.[0];
			if (!layerId) return customization;

			const layer = layerMap.get(layerId);
			if (layer?.type === LayerType.VIDEO && !customization.defaultValue && customization.type === CustomizationType.VIDEO_PICKER) {
				return {
					...customization,
					value: undefined
				};
			}
			return customization;
		});

		return {
			...pkg,
			customizations: finalCustomizations
		};
	}

	/**
	 * This method will return the publishe packaged that is most recent in the version list or fallback to the first version.
	*/
	public static getLatestVersion(versions: CreativeUnitPackageDto[]): CreativeUnitPackageDto {
		if (versions.length === 0) {
			return null;
		}
		const sortedPublished = [...versions].filter(x=> x.status === CreativeUnitStatus.Published ).sort(
			(a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime()
		  );
		return sortedPublished[0] || versions[0];

	}
}
